<script setup lang="ts">
import type { PendoRating, PendoTrackData } from '~/models/Pendo'
import { useI18n } from 'vue-i18n'
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faThumbsDown, faThumbsUp } from '@fortawesome/duotone-regular-svg-icons'
import { AuToast } from '~/stores/toasts'
import { useAuthStore } from '~/stores/auth'
import { PendoTrackName } from '~/models/Pendo'
import usePendo from '~/composables/usePendo'
import Button from '~/components/ui/Button.vue'

const props = defineProps<{
  context: string
  data: string
}>()

const { t } = useI18n()
const { pendoTrack, pendoFlushNow } = usePendo()
const { isTeacher } = storeToRefs(useAuthStore())

const feedbackRating = ref<PendoRating>('neutral')
const submitted = ref<boolean>(false)

const messageText = computed(() => {
  if (feedbackRating.value === 'positive')
    return t('feedback.positive')
  if (feedbackRating.value === 'negative')
    return t('feedback.negative', { email: import.meta.env.VITE_SUPPORT_EMAIL })
  return ''
})

const hasRated = computed(() => feedbackRating.value === 'positive' || feedbackRating.value === 'negative')
const showFeedback = computed(() => isTeacher.value && !submitted.value)

watch(() => props.data, (newData, oldData) => {
  if (newData !== oldData)
    reset()
})

const reset = () => {
  feedbackRating.value = 'neutral'
  submitted.value = false
}

const handleFeedbackButtons = (rating: PendoRating) => {
  if (feedbackRating.value !== 'neutral')
    return
  feedbackRating.value = rating
  const ratingData: PendoTrackData = {
    context: props.context,
    rating,
    data: props.data,
    url: window.location.href,
  }
  pendoTrack(PendoTrackName.Rating, ratingData)
  pendoFlushNow()
  AuToast.success(t('feedback.event.rated'))
}

const copyUrl = () => {
  try {
    navigator.clipboard.writeText(window.location.href)
    AuToast.success(t('feedback.event.copied'))
  } catch (error) {
    AuToast.error(t('feedback.event.copyFailed'))
    throw error
  }
}
</script>

<template>
  <Transition
    enter-from-class="transform opacity-0"
    enter-active-class="duration-1000 ease delay-500"
    enter-to-class="opacity-100"
    leave-from-class="opacity-100"
    leave-active-class="duration-1000 ease-out"
    leave-to-class="opacity-0"
    appear
  >
    <section
      v-if="showFeedback"
      id="feedback-section"
      class="text-green-5 mx-auto bg-green-50 p-10 transition-all"
    >
      <div class="mx-auto max-w-screen-md">
        <div class="mb-10 flex-wrap items-center gap-3 sm:flex">
          <h3
            class="w-full text-xl font-bold"
            v-text="t('feedback.question')"
          />
          <Button
            :class="{ 'pointer-events-none': feedbackRating !== 'neutral' }"
            :shade="feedbackRating === 'positive' ? 'dark' : 'light'"
            shape="square"
            size="xl"
            @click="handleFeedbackButtons('positive')"
          >
            <font-awesome-icon :icon="faThumbsUp" />
          </Button>
          <Button
            :class="{ 'pointer-events-none': feedbackRating !== 'neutral' }"
            :shade="feedbackRating === 'negative' ? 'dark' : 'light'"
            shape="square"
            size="xl"
            @click="handleFeedbackButtons('negative')"
          >
            <font-awesome-icon :icon="faThumbsDown" />
          </Button>
        </div>
        <div
          v-if="hasRated"
          class="flex-wrap items-center gap-3 sm:flex"
        >
          <p
            class="text-base"
            v-html="messageText"
          />
          <Button
            v-if="feedbackRating === 'negative'"
            size="lg"
            @click="copyUrl"
          >
            {{ t('feedback.copyUrl') }}
          </Button>
        </div>
      </div>
    </section>
  </Transition>
</template>
